import { useState } from 'react'
import StatusChip from '../../components/ui/StatusChip'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Block as DisableIcon,
  Clear as ClearIcon,
  Print as PrintIcon,
  Upload as UploadIcon,
} from '@mui/icons-material'
import Table, { Column } from '../../components/ui/Table'
import { useCustomers, useDisableCustomer, useExportCustomers } from '../../hooks/api/useCustomers'
import { useBookers } from '../../hooks/api/useBookers'
import { useAreas } from '../../hooks/api/useAreas'
import { Customer } from '../../types'
import { format } from 'date-fns'
import CustomerForm from '../../components/forms/CustomerForm'
import BulkImportDialog from '../../components/forms/BulkImportDialog'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { formatCurrency } from '../../utils/formatters'

const CustomerListPage = () => {
  const user = useSelector((state: RootState) => state.auth.user)
  const cartonDiscountPricingEnabled = user?.company_features?.carton_discount_pricing === true
  const advanceTaxEnabled = user?.company_features?.advance_tax !== false
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [search, setSearch] = useState('')
  const [filerFilter, setFilerFilter] = useState('')
  const [bookerFilter, setBookerFilter] = useState<string[]>([])
  const [areaFilter, setAreaFilter] = useState<string[]>([])
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)

  const { data: customersData, isLoading } = useCustomers({
    page: page + 1,
    limit: rowsPerPage,
    search,
    is_filer: filerFilter === 'filer' ? true : filerFilter === 'non-filer' ? false : undefined,
    booker_id: bookerFilter.length > 0 ? bookerFilter : undefined,
    area_id: areaFilter.length > 0 ? areaFilter : undefined,
  })

  const { data: bookersData } = useBookers({ limit: 100 })
  const { data: areasData } = useAreas(0, 200)
  const disableCustomerMutation = useDisableCustomer()
  const exportCustomersMutation = useExportCustomers()

  const handlePrint = () => {
    exportCustomersMutation.mutate({
      search: search || undefined,
      is_filer: filerFilter === 'filer' ? true : filerFilter === 'non-filer' ? false : undefined,
      booker_id: bookerFilter.length > 0 ? bookerFilter : undefined,
      area_id: areaFilter.length > 0 ? areaFilter : undefined,
    })
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value)
    setPage(0)
  }

  const handleFilerFilter = (event: any) => {
    setFilerFilter(event.target.value)
    setPage(0)
  }

  const handleBookerFilter = (event: any) => {
    const value = event.target.value
    setBookerFilter(typeof value === 'string' ? value.split(',') : value)
    setPage(0)
  }

  const handleAreaFilter = (event: any) => {
    const value = event.target.value
    setAreaFilter(typeof value === 'string' ? value.split(',') : value)
    setPage(0)
  }

  const handleResetFilters = () => {
    setSearch('')
    setFilerFilter('')
    setBookerFilter([])
    setAreaFilter([])
    setPage(0)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, customer: Customer) => {
    setAnchorEl(event.currentTarget)
    setSelectedCustomer(customer)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedCustomer(null)
  }

  const handleCreateCustomer = () => {
    setFormMode('create')
    setSelectedCustomer(null)
    setFormOpen(true)
  }

  const handleEditCustomer = () => {
    setFormMode('edit')
    setFormOpen(true)
    setAnchorEl(null) // Just close the menu
  }

  const handleDelete = () => {
    setDeleteDialogOpen(true)
    setAnchorEl(null)
  }

  const handleDeleteConfirm = () => {
    if (selectedCustomer) {
      disableCustomerMutation.mutate(selectedCustomer._id)
    }
    setDeleteDialogOpen(false)
    setSelectedCustomer(null)
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setSelectedCustomer(null)
  }

  const handleFormClose = () => {
    setFormOpen(false)
    setSelectedCustomer(null)
  }

  const columns: Column<Customer>[] = [
    {
      id: 'area_id',
      label: 'Area',
      minWidth: 100,
      render: (value) => value?.area_name || '-',
    },
    {
      id: 'customer_code',
      label: 'Customer Code',
      minWidth: 140,
      render: (value) => value || '-',
    },
    {
      id: 'name',
      label: 'Customer Name',
      minWidth: 200,
    },
    {
      id: 'booker_id',
      label: 'Booker',
      minWidth: 150,
      render: (value) => {
        return value?.name || 'Unknown'
      },
    },
    {
      id: 'cnic',
      label: 'CNIC / NTN',
      minWidth: 200,
      render: (value, customer) =>
        (customer.is_sales_tax_customer ? customer.ntn : value) || '-',
    },
    {
      id: 'phone',
      label: 'Phone',
      minWidth: 150,
      render: (value) => value || '-',
    },
    {
      id: 'outstanding_balance',
      label: 'Total Outstanding',
      minWidth: 170,
      align: 'right',
      render: (value) => `Rs ${formatCurrency(value)}`,
    },
    ...(advanceTaxEnabled ? ([{
      id: 'is_filer',
      label: 'Filer Status',
      minWidth: 120,
      render: (value) => (
        <StatusChip
          label={value ? 'Filer' : 'Non-Filer'}
          variant={value ? 'success' : 'neutral'}
        />
      ),
    }] as Column<Customer>[]) : []),
    ...(!cartonDiscountPricingEnabled ? ([{
      id: 'discount',
      label: 'Discount %',
      minWidth: 100,
      align: 'right',
      render: (value) => `${value}%`,
    }] as Column<Customer>[]) : []),
    {
      id: 'updatedAt',
      label: 'Last Updated',
      minWidth: 180,
      render: (value) => format(new Date(value), 'MMM dd, yyyy hh:mm a'),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 100,
      align: 'center',
      render: (_, row) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, row)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ]

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Customers</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={exportCustomersMutation.isPending}
          >
            {exportCustomersMutation.isPending ? 'Loading...' : 'Print / Export'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setImportOpen(true)}
          >
            Import CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateCustomer}
          >
            Create Customer
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search by customer name or code..."
              value={search}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Filer Status</InputLabel>
              <Select
                value={filerFilter}
                onChange={handleFilerFilter}
                label="Filer Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="filer">Filer</MenuItem>
                <MenuItem value="non-filer">Non-Filer</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth>
              <InputLabel>Area</InputLabel>
              <Select
                multiple
                value={areaFilter}
                onChange={handleAreaFilter}
                label="Area"
                renderValue={(selected) => {
                  if (selected.length === 0) return 'All Areas'
                  if (selected.length === 1) {
                    const area = areasData?.results?.find((a: any) => a._id === selected[0])
                    return area?.area_name || selected[0]
                  }
                  return `${selected.length} areas selected`
                }}
              >
                {areasData?.results?.map((area: any) => (
                  <MenuItem key={area._id} value={area._id}>
                    {area.area_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth>
              <InputLabel>Booker</InputLabel>
              <Select
                multiple
                value={bookerFilter}
                onChange={handleBookerFilter}
                label="Booker"
                renderValue={(selected) => {
                  if (selected.length === 0) return 'All Bookers'
                  if (selected.length === 1) {
                    const booker = bookersData?.data?.find(b => b._id === selected[0])
                    return booker?.name || selected[0]
                  }
                  return `${selected.length} bookers selected`
                }}
              >
                <MenuItem value="">
                  <em>All Bookers</em>
                </MenuItem>
                {bookersData?.data?.map((booker) => (
                  <MenuItem key={booker._id} value={booker._id}>
                    {booker.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex' }}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleResetFilters}
              fullWidth
            >
              Reset Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Table
        columns={columns}
        onRowClick={(customer) => navigate(`/customers/${customer._id}`)}
        data={customersData?.results?.map(customer => ({ ...customer, id: customer._id })) || []}
        loading={isLoading}
        pagination={customersData ? {
          page,
          rowsPerPage,
          total: customersData.info.total,
          onPageChange: setPage,
          onRowsPerPageChange: setRowsPerPage,
        } : undefined}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditCustomer}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'warning.main' }}>
          <DisableIcon sx={{ mr: 1 }} />
          Disable
        </MenuItem>
      </Menu>

      <CustomerForm
        open={formOpen}
        onClose={handleFormClose}
        customer={selectedCustomer}
        mode={formMode}
      />

      <BulkImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        entity="customers"
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Disable Customer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Disable the customer "{selectedCustomer?.name}"? Existing bills will be preserved, but the customer cannot be used for new bookings.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="warning" variant="contained">
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CustomerListPage
